import { useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, Building2, Plus, Search, User, X } from 'lucide-react';
import {
  prospectsApi,
  type ProspectDetail,
  type ProspectListItem,
  type ProspectType,
} from '@/services/prospects.api';
import { configApi, type ContractType } from '@/services/config.api';
import { jeopardyApi, type JeopardyEntry } from '@/services/jeopardy.api';
import { useAuthStore } from '@/stores/auth';
import { Button } from '@/components/ui/Button';
import { Input, Label, Select, Textarea } from '@/components/ui/Input';
import { Banner } from '@/components/ui/Banner';
import { useFocusTrap } from '@/hooks/useFocusTrap';
import { useHotkey } from '@/hooks/useHotkey';
import { CreateProspectModal } from './CreateProspectModal';
import { displayName } from '@shared/prospect';

interface ProspectOption {
  id: string;
  type: ProspectType;
  displayName: string;
  siret: string | null;
}

interface Props {
  onClose: () => void;
  onSaved: (entry: JeopardyEntry) => void;
  /** Entrée à éditer. Si fournie, la modale passe en mode édition : le client
   *  est figé (lecture seule) et tous les autres champs sont pré-remplis. */
  initialEntry?: JeopardyEntry | null;
  /** Client pré-sélectionné (mode création depuis une fiche prospect). */
  initialProspect?: ProspectOption | null;
}

function toOption(p: ProspectListItem | ProspectDetail): ProspectOption {
  return { id: p.id, type: p.type, displayName: displayName(p), siret: p.siret };
}

/**
 * Modale « Signaler / modifier un contrat client en danger » (Client Jeopardy).
 *
 * Mode création : le client est choisi via une combobox de recherche async
 * (debounce 250 ms sur `prospectsApi.list({ search })`), avec un bouton
 * « Nouveau client » qui empile une `CreateProspectModal` par-dessus (z-[60]) —
 * même système que `CreateOpportunityModal`. Le combobox est ici volontairement
 * ré-implémenté en local (composant auto-suffisant) pour ne pas modifier la
 * modale d'opportunité déjà livrée.
 *
 * Mode édition (`initialEntry`) : le client est figé (on ne déplace pas une
 * entrée d'un périmètre de cloisonnement à un autre) ; tous les autres champs
 * restent modifiables.
 *
 * Un avertissement NON bloquant s'affiche si un contrat portant le même numéro
 * existe déjà pour ce client (`duplicate-check`).
 */
export function CreateJeopardyModal({
  onClose,
  onSaved,
  initialEntry = null,
  initialProspect = null,
}: Props) {
  const token = useAuthStore((s) => s.accessToken)!;
  const isEdit = Boolean(initialEntry);
  const dialogRef = useRef<HTMLDivElement | null>(null);

  const [showCreateProspect, setShowCreateProspect] = useState(false);
  useFocusTrap(!showCreateProspect, dialogRef);
  useHotkey('escape', onClose, { allowInInput: true, enabled: !showCreateProspect });

  const [contractTypes, setContractTypes] = useState<ContractType[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const cts = await configApi.listContractTypes(token);
        if (!cancelled) setContractTypes(cts.filter((c) => c.isActive));
      } catch {
        /* les erreurs remontent au submit via les champs requis */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const [selectedProspect, setSelectedProspect] = useState<ProspectOption | null>(
    initialEntry
      ? {
          id: initialEntry.prospect.id,
          type: initialEntry.prospect.type,
          displayName: displayName(initialEntry.prospect),
          siret: null,
        }
      : initialProspect,
  );
  const [contractNumber, setContractNumber] = useState(initialEntry?.contractNumber ?? '');
  const [contractTypeId, setContractTypeId] = useState(initialEntry?.contractType.id ?? '');
  const [annualPremiumHt, setAnnualPremiumHt] = useState(
    initialEntry ? String(Number(initialEntry.annualPremiumHt)) : '',
  );
  const [annualCommission, setAnnualCommission] = useState(
    initialEntry ? String(Number(initialEntry.annualCommission)) : '',
  );
  const [dueDate, setDueDate] = useState(initialEntry ? initialEntry.dueDate.slice(0, 10) : '');
  const [reason, setReason] = useState(initialEntry?.reason ?? '');

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dupCount, setDupCount] = useState(0);

  // Filtre les types de contrat selon le type de prospect (comme l'opportunité).
  const filteredContractTypes = useMemo(() => {
    if (!contractTypes) return [];
    if (!selectedProspect) return contractTypes;
    return contractTypes.filter((c) =>
      selectedProspect.type === 'individual' ? c.applicableToIndividual : c.applicableToCompany,
    );
  }, [contractTypes, selectedProspect]);

  useEffect(() => {
    if (
      contractTypeId &&
      filteredContractTypes.length > 0 &&
      !filteredContractTypes.some((c) => c.id === contractTypeId)
    ) {
      setContractTypeId('');
    }
  }, [contractTypeId, filteredContractTypes]);

  // Vérification de doublon non bloquante (debounce 350 ms). Exclut l'entrée
  // courante en édition. N'empêche jamais la soumission.
  useEffect(() => {
    const num = contractNumber.trim();
    if (!selectedProspect || num.length === 0) {
      setDupCount(0);
      return;
    }
    const handle = setTimeout(async () => {
      try {
        const res = await jeopardyApi.duplicateCheck(
          token,
          selectedProspect.id,
          num,
          initialEntry?.id,
        );
        setDupCount(res.count);
      } catch {
        setDupCount(0);
      }
    }, 350);
    return () => clearTimeout(handle);
  }, [selectedProspect, contractNumber, token, initialEntry]);

  const premiumNum = Number(annualPremiumHt);
  const commissionNum = Number(annualCommission);

  const canSubmit = useMemo(
    () =>
      !submitting &&
      !!selectedProspect &&
      contractNumber.trim().length > 0 &&
      !!contractTypeId &&
      annualPremiumHt !== '' &&
      Number.isFinite(premiumNum) &&
      premiumNum >= 0 &&
      annualCommission !== '' &&
      Number.isFinite(commissionNum) &&
      commissionNum >= 0 &&
      !!dueDate &&
      reason.trim().length > 0,
    [
      submitting,
      selectedProspect,
      contractNumber,
      contractTypeId,
      annualPremiumHt,
      premiumNum,
      annualCommission,
      commissionNum,
      dueDate,
      reason,
    ],
  );

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit || !selectedProspect) return;
    setSubmitting(true);
    setError(null);
    try {
      const fields = {
        contractNumber: contractNumber.trim(),
        contractTypeId,
        annualPremiumHt: premiumNum,
        annualCommission: commissionNum,
        dueDate,
        reason: reason.trim(),
      };
      const saved = initialEntry
        ? await jeopardyApi.update(token, initialEntry.id, fields)
        : await jeopardyApi.create(token, { prospectId: selectedProspect.id, ...fields });
      onSaved(saved);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-brand/30 px-4 backdrop-blur-sm animate-fade-in"
        role="presentation"
      >
        <div
          ref={dialogRef}
          role="dialog"
          aria-modal="true"
          aria-label={isEdit ? 'Modifier le signalement' : 'Signaler un contrat en danger'}
          onClick={(e) => e.stopPropagation()}
          className="max-h-[90vh] w-full max-w-xl overflow-y-auto rounded-lg bg-white shadow-modal animate-scale-in"
        >
          <form onSubmit={handleSubmit} className="space-y-5 p-6">
            <div className="flex items-center justify-between border-b border-border-subtle pb-4">
              <div>
                <h2 className="text-lg font-semibold tracking-tight text-brand">
                  {isEdit ? 'Modifier le signalement' : 'Signaler un contrat en danger'}
                </h2>
                <p className="mt-0.5 text-xs text-text-muted">
                  Un contrat client en vigueur identifié comme à risque de perte.
                </p>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="rounded-md p-1 text-text-muted hover:bg-gray-100 hover:text-brand"
                aria-label="Fermer"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Client : combobox en création, lecture seule en édition. */}
            <div>
              <div className="mb-1.5 flex items-baseline justify-between">
                <Label htmlFor="jeo-prospect" required>
                  Client
                </Label>
                {!isEdit && (
                  <button
                    type="button"
                    onClick={() => setShowCreateProspect(true)}
                    className="inline-flex items-center gap-1 text-[11px] font-medium text-text-muted transition-colors hover:text-accent focus-visible:text-accent"
                  >
                    <Plus className="h-3 w-3" />
                    Nouveau client
                  </button>
                )}
              </div>
              {isEdit && selectedProspect ? (
                <div className="flex items-center gap-2 rounded-md border border-border-subtle bg-gray-50 px-3 py-2">
                  {selectedProspect.type === 'individual' ? (
                    <User className="h-4 w-4 shrink-0 text-text-muted" />
                  ) : (
                    <Building2 className="h-4 w-4 shrink-0 text-text-muted" />
                  )}
                  <span className="truncate text-sm font-medium text-brand">
                    {selectedProspect.displayName}
                  </span>
                </div>
              ) : (
                <ProspectCombobox
                  token={token}
                  value={selectedProspect}
                  onChange={setSelectedProspect}
                  inputId="jeo-prospect"
                />
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="jeo-contract-number" required>
                  Numéro de contrat
                </Label>
                <Input
                  id="jeo-contract-number"
                  value={contractNumber}
                  onChange={(e) => setContractNumber(e.target.value)}
                  maxLength={100}
                  placeholder="Ex. POL-2024-004512"
                  required
                />
              </div>
              <div>
                <Label htmlFor="jeo-contract-type" required>
                  Type de contrat
                </Label>
                <Select
                  id="jeo-contract-type"
                  value={contractTypeId}
                  onChange={(e) => setContractTypeId(e.target.value)}
                  disabled={!contractTypes}
                  required
                >
                  <option value="">{contractTypes ? '- Sélectionner -' : 'Chargement…'}</option>
                  {filteredContractTypes.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.code} - {c.label}
                    </option>
                  ))}
                </Select>
                {selectedProspect && filteredContractTypes.length === 0 && contractTypes && (
                  <p className="mt-1 text-[11px] text-warning-600">
                    Aucun type de contrat n&apos;est configuré pour un{' '}
                    {selectedProspect.type === 'individual' ? 'particulier' : 'entreprise'}.
                  </p>
                )}
              </div>
            </div>

            {/* Avertissement doublon NON bloquant. */}
            {dupCount > 0 && (
              <div className="flex items-start gap-2 rounded-md border border-warning-100 bg-warning-50 px-3 py-2 text-xs text-warning-600">
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                <span>
                  Un contrat portant ce numéro est déjà signalé pour ce client. Vous pouvez tout de
                  même l&apos;ajouter.
                </span>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="jeo-premium" required>
                  Prime annuelle HT (€)
                </Label>
                <Input
                  id="jeo-premium"
                  type="number"
                  step="0.01"
                  min="0"
                  value={annualPremiumHt}
                  onChange={(e) => setAnnualPremiumHt(e.target.value)}
                  placeholder="0,00"
                  required
                />
              </div>
              <div>
                <Label htmlFor="jeo-commission" required>
                  Commission annuelle (€)
                </Label>
                <Input
                  id="jeo-commission"
                  type="number"
                  step="0.01"
                  min="0"
                  value={annualCommission}
                  onChange={(e) => setAnnualCommission(e.target.value)}
                  placeholder="0,00"
                  required
                />
              </div>
            </div>

            <div>
              <Label htmlFor="jeo-due-date" required>
                Date d&apos;échéance du contrat
              </Label>
              <Input
                id="jeo-due-date"
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                required
              />
            </div>

            <div>
              <Label htmlFor="jeo-reason" required>
                Raison de la mise en danger
              </Label>
              <Textarea
                id="jeo-reason"
                rows={4}
                maxLength={5000}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Ex. le client compare avec un concurrent, sinistralité en hausse, insatisfaction sur la gestion…"
                required
              />
              <p className="mt-1 text-[11px] text-text-muted">{reason.length}/5000 caractères</p>
            </div>

            {error && <Banner tone="danger">{error}</Banner>}

            <div className="flex justify-end gap-2 border-t border-border-subtle pt-4">
              <Button variant="secondary" onClick={onClose}>
                Annuler
              </Button>
              <Button type="submit" variant="primary" loading={submitting} disabled={!canSubmit}>
                {submitting ? 'Enregistrement…' : isEdit ? 'Enregistrer' : 'Ajouter à la liste'}
              </Button>
            </div>
          </form>
        </div>
      </div>

      {showCreateProspect && (
        <CreateProspectModal
          zClass="z-[60]"
          onClose={() => setShowCreateProspect(false)}
          onCreated={(p: ProspectDetail) => {
            setShowCreateProspect(false);
            setSelectedProspect(toOption(p));
          }}
        />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Combobox prospect - recherche async debounced (copie auto-suffisante du
// pattern de CreateOpportunityModal, pour ne pas modifier ce fichier livré).
// ---------------------------------------------------------------------------

function ProspectCombobox({
  token,
  value,
  onChange,
  inputId,
}: {
  token: string;
  value: ProspectOption | null;
  onChange: (p: ProspectOption | null) => void;
  inputId: string;
}) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<ProspectListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const term = query.trim();
    if (term.length < 2) {
      setResults([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const handle = setTimeout(async () => {
      try {
        const res = await prospectsApi.list(token, { search: term, limit: 10 });
        setResults(res.data);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(handle);
  }, [query, token, open]);

  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  return (
    <div ref={containerRef} className="relative">
      {value ? (
        <div className="flex items-center justify-between gap-2 rounded-md border border-border-subtle bg-white px-3 py-2 shadow-[inset_0_1px_2px_rgba(27,42,71,0.04)]">
          <div className="flex min-w-0 items-center gap-2">
            {value.type === 'individual' ? (
              <User className="h-4 w-4 shrink-0 text-text-muted" />
            ) : (
              <Building2 className="h-4 w-4 shrink-0 text-text-muted" />
            )}
            <div className="min-w-0">
              <div className="truncate text-sm font-medium text-brand">{value.displayName}</div>
              <div className="truncate font-mono text-[11px] text-text-muted">
                {value.siret ?? (value.type === 'individual' ? 'Particulier' : '')}
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={() => {
              onChange(null);
              setQuery('');
              setOpen(true);
            }}
            className="shrink-0 rounded p-1 text-text-muted hover:bg-gray-100 hover:text-brand"
            aria-label="Changer de client"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      ) : (
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-text-muted" />
          <Input
            id={inputId}
            value={query}
            onFocus={() => setOpen(true)}
            onChange={(e) => {
              setQuery(e.target.value);
              setOpen(true);
            }}
            placeholder="Rechercher par nom, SIRET, email ou téléphone…"
            className="pl-8"
            autoComplete="off"
          />
        </div>
      )}

      {open && !value && (
        <div className="absolute left-0 right-0 top-full z-10 mt-1 max-h-64 overflow-y-auto rounded-md border border-border-subtle bg-white shadow-popover">
          {query.trim().length < 2 ? (
            <div className="px-3 py-3 text-xs text-text-muted">
              Tapez au moins 2 caractères pour rechercher.
            </div>
          ) : loading ? (
            <div className="px-3 py-3 text-xs text-text-muted">Recherche…</div>
          ) : results.length === 0 ? (
            <div className="px-3 py-3 text-xs text-text-muted">
              Aucun client trouvé pour « {query.trim()} ».
            </div>
          ) : (
            <ul>
              {results.map((p) => (
                <li key={p.id}>
                  <button
                    type="button"
                    onClick={() => {
                      onChange(toOption(p));
                      setOpen(false);
                      setQuery('');
                    }}
                    className="flex w-full items-start gap-2 px-3 py-2 text-left hover:bg-gray-50"
                  >
                    {p.type === 'individual' ? (
                      <User className="mt-0.5 h-4 w-4 shrink-0 text-text-muted" />
                    ) : (
                      <Building2 className="mt-0.5 h-4 w-4 shrink-0 text-text-muted" />
                    )}
                    <div className="flex flex-col items-start gap-0">
                      <span className="text-sm font-medium text-brand">{displayName(p)}</span>
                      <span className="font-mono text-[11px] text-text-muted">
                        {p.siret ?? p.personalEmail ?? 'Particulier'}
                      </span>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
